import React, { useEffect, useMemo, useState } from 'react';
import {
  Ban,
  CalendarOff,
  CreditCard,
  MessageSquareOff,
  Loader2,
  Filter,
} from 'lucide-react';
import { auth } from '../../../../firebase';
import {
  formatRelativeTime,
  formatSkipSurface,
  formatAuditKind,
} from '../../../../utils/accountLifecycle';

const API = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 50;

const KIND_ICONS = {
  recurrence_skipped: CalendarOff,
  autocharge_skipped: CreditCard,
  outbound_skipped: MessageSquareOff,
};

const KIND_TONE = {
  recurrence_skipped: 'text-purple-600 bg-purple-50 border-purple-200',
  autocharge_skipped: 'text-amber-700 bg-amber-50 border-amber-200',
  outbound_skipped: 'text-sky-700 bg-sky-50 border-sky-200',
};

const FILTERS = [
  { id: null, label: 'All' },
  { id: 'recurrence_skipped', label: 'Recurrence' },
  { id: 'autocharge_skipped', label: 'Auto-charge' },
  { id: 'outbound_skipped', label: 'Outbound' },
];

/**
 * LifecycleTab — Phase E read-only feed of lifecycle audit events.
 *
 * Reads `GET /api/leads/{leadId}/lifecycle-audit` and renders a
 * chronological timeline of recurrence_skipped / autocharge_skipped /
 * outbound_skipped events. Purely informational; no actions, no replay.
 *
 * Visible to all roles (admin/sales/technician). The tab itself is
 * inherently read-only — no controls mutate state from this surface.
 */
const LifecycleTab = ({ client }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeKind, setActiveKind] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!client?.id) return;
      setLoading(true);
      setError(null);
      try {
        const token = await auth.currentUser?.getIdToken();
        const qs = new URLSearchParams({ limit: String(PAGE_SIZE) });
        if (activeKind) qs.set('kind', activeKind);
        const res = await fetch(
          `${API}/api/leads/${client.id}/lifecycle-audit?${qs.toString()}`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} }
        );
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok) {
          setError(data.detail || 'Failed to load lifecycle events');
          setEntries([]);
        } else {
          setEntries(Array.isArray(data.entries) ? data.entries : []);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('[LifecycleTab] load error:', err);
          setError('Network error — please try again');
          setEntries([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [client?.id, activeKind]);

  const grouped = useMemo(() => {
    // Group by relative day for visual chunking. We trust the API's
    // descending `at` order.
    const out = [];
    let lastBucket = null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    for (const entry of entries) {
      const d = entry.at ? new Date(entry.at) : null;
      let bucket = 'Earlier';
      if (d && !Number.isNaN(d.getTime())) {
        const day = new Date(d);
        day.setHours(0, 0, 0, 0);
        if (day.getTime() === today.getTime()) bucket = 'Today';
        else if (day.getTime() === yesterday.getTime()) bucket = 'Yesterday';
        else
          bucket = d.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: d.getFullYear() === today.getFullYear() ? undefined : 'numeric',
          });
      }
      if (bucket !== lastBucket) {
        out.push({ kind: 'header', label: bucket });
        lastBucket = bucket;
      }
      out.push({ kind: 'entry', entry });
    }
    return out;
  }, [entries]);

  return (
    <div className="space-y-4" data-testid="lifecycle-tab">
      {/* Filter row */}
      <div className="flex items-center gap-2 flex-wrap">
        <Filter className="h-3.5 w-3.5 text-gray-400" />
        {FILTERS.map((f) => {
          const isActive = activeKind === f.id;
          return (
            <button
              key={f.id || 'all'}
              type="button"
              onClick={() => setActiveKind(f.id)}
              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                isActive
                  ? 'border-orange-300 bg-orange-50 text-orange-700'
                  : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
              }`}
              data-testid={`lifecycle-filter-${f.id || 'all'}`}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      {/* Body */}
      {loading && (
        <div className="flex items-center gap-2 text-sm text-gray-500 py-8 justify-center">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading lifecycle events…
        </div>
      )}

      {!loading && error && (
        <div className="text-sm text-red-600 py-8 text-center" data-testid="lifecycle-error">
          {error}
        </div>
      )}

      {!loading && !error && entries.length === 0 && (
        <div className="text-center py-12 px-4" data-testid="lifecycle-empty">
          <Ban className="h-8 w-8 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-600 font-medium">No lifecycle events recorded</p>
          <p className="text-xs text-gray-400 mt-1 max-w-sm mx-auto">
            Events appear here when an account is Inactive or Closed and the platform
            skips an automatic action (recurrence seeding, auto-charge, or automated SMS).
          </p>
        </div>
      )}

      {!loading && !error && entries.length > 0 && (
        <ol className="space-y-2">
          {grouped.map((row, idx) =>
            row.kind === 'header' ? (
              <li
                key={`h-${row.label}-${idx}`}
                className="text-[11px] uppercase tracking-wider text-gray-400 pt-3 pb-1"
              >
                {row.label}
              </li>
            ) : (
              <LifecycleEventRow key={row.entry.id} entry={row.entry} />
            )
          )}
        </ol>
      )}
    </div>
  );
};

const LifecycleEventRow = ({ entry }) => {
  const Icon = KIND_ICONS[entry.kind] || Ban;
  const tone = KIND_TONE[entry.kind] || 'text-gray-600 bg-gray-50 border-gray-200';
  const when = formatRelativeTime(entry.at) || '';

  let detail = null;
  if (entry.kind === 'outbound_skipped') {
    detail = (
      <>
        <span className="font-medium">{formatSkipSurface(entry.surface)}</span>
        {entry.templateKey && (
          <span className="text-gray-500"> · template: {entry.templateKey}</span>
        )}
      </>
    );
  } else if (entry.kind === 'autocharge_skipped') {
    detail = (
      <>
        Invoice {entry.invoiceId || '—'}
        {entry.amount != null && (
          <span className="text-gray-500"> · ${Number(entry.amount).toFixed(2)}</span>
        )}
      </>
    );
  } else if (entry.kind === 'recurrence_skipped') {
    detail = (
      <>
        After job {entry.completedJobId || '—'}
      </>
    );
  } else {
    detail = entry.surface || '';
  }

  return (
    <li
      className={`flex items-start gap-3 rounded-md border ${tone} px-3 py-2 text-sm`}
      data-testid={`lifecycle-event-${entry.id}`}
    >
      <Icon className="h-4 w-4 mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-3">
          <span className="font-medium">{formatAuditKind(entry.kind)}</span>
          <span className="text-[11px] text-gray-500 shrink-0">{when}</span>
        </div>
        <div className="text-xs text-gray-700 mt-0.5 truncate">{detail}</div>
        <div className="text-[11px] text-gray-400 mt-0.5">
          Account status at the time:{' '}
          <span className="font-medium text-gray-600 capitalize">
            {entry.accountStatus === 'paused' ? 'inactive' : (entry.accountStatus || 'unknown')}
          </span>
        </div>
      </div>
    </li>
  );
};

export default LifecycleTab;
